import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiTrendingUp, FiZap, FiCheck, FiLogIn, FiTarget, FiBarChart2, FiStar, FiRefreshCw } = FiIcons;

const LandingPage = ({ onGetStarted, onLogin }) => {
  const features = [
    { icon: FiShield, title: 'Preserve Rankings', text: 'Search engines index your original page normally' },
    { icon: FiTarget, title: 'Route Real Visitors', text: 'Humans get redirected to your high-converting offers' },
    { icon: FiZap, title: 'Setup in Minutes', text: 'No server changes or dev work required' },
    { icon: FiBarChart2, title: 'Unlimited Scale', text: 'Works on unlimited pages and domains' }
  ];

  const benefits = [
    'Unlimited Pages & Redirects',
    'Instant Activation',
    'Monthly Updates & Support',
    'Cancel Anytime',
    'Secure Encrypted Snippets',
    'Cloud Dashboard Access'
  ];

  const testimonials = [
    {
      quote: "Added 6 figures by keeping rankings on old domains while redirecting to fresh offers. Game-changer!",
      author: "Alex T.",
      role: "Affiliate Pro"
    },
    {
      quote: "Bulk tool saved weeks on my network. Instant revenue boost.",
      author: "Jenna M.",
      role: "SEO Expert"
    }
  ];

  return (
    <div className="min-h-screen px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -30 }}
        transition={{ duration: 0.6 }}
        className="max-w-6xl mx-auto"
      >
        {/* Top Navigation Bar with Login Button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-between items-center mb-12"
        >
          <div className="flex items-center space-x-2">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-2 rounded-lg">
              <SafeIcon icon={FiZap} className="w-6 h-6 text-white" />
            </div>
            <span className="text-white font-bold text-xl">Sneaky Redirects</span>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onLogin}
            className="flex items-center space-x-2 px-6 py-3 bg-slate-700/50 hover:bg-slate-700/70 text-slate-200 font-medium rounded-lg transition-all duration-200 border border-slate-600/50"
          >
            <SafeIcon icon={FiLogIn} className="w-5 h-5" />
            <span>Sign In</span>
          </motion.button>
        </motion.div>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block bg-blue-600/20 px-4 py-2 rounded-full mb-6 border border-blue-500/30"
          >
            <p className="text-blue-300 text-sm font-semibold uppercase tracking-wide">
              The Ultimate Tool for Smart Traffic Control
            </p>
          </motion.div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
              Sneaky Redirects
            </span>
            <br />
            <span className="text-white text-3xl md:text-5xl lg:text-6xl">
              Unlock Hidden Traffic Profits Instantly
            </span>
          </h1>
          
          <p className="text-slate-300 text-xl md:text-2xl mb-4 max-w-4xl mx-auto leading-relaxed">
            Keep Your Pages Ranked & Indexed in Google... While Quietly Sending Real Visitors Anywhere You Want
          </p>
          
          <p className="text-slate-400 text-lg max-w-3xl mx-auto">
            As a savvy marketer, you know ranked pages are gold. But what if you could monetize that traffic better—by routing real humans to high-converting offers, affiliates, or funnels—<strong className="text-slate-300">without losing your SEO rankings?</strong>
          </p>
        </div>

        {/* Value Proposition */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 md:p-12 mb-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-slate-300 text-lg">
              Add one lightweight JavaScript snippet to any page you control (HTML access required), and:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700/30 hover:border-blue-500/30 transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600/20 p-3 rounded-xl flex-shrink-0">
                    <SafeIcon icon={feature.icon} className="w-7 h-7 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-slate-400">{feature.text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-blue-600/10 to-purple-600/10 border border-blue-500/20 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4 text-center">Setup in Minutes</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="bg-blue-600/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-400 font-bold text-xl">1</div>
                <p className="text-slate-300">Enter your target redirect URL in our cloud dashboard</p>
              </div>
              <div>
                <div className="bg-blue-600/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-400 font-bold text-xl">2</div>
                <p className="text-slate-300">Generate your secure, encrypted JS snippet</p>
              </div>
              <div>
                <div className="bg-blue-600/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-400 font-bold text-xl">3</div>
                <p className="text-slate-300">Paste it into your page(s). Done!</p>
              </div>
            </div>
          </div>
        </div>

        {/* Why Marketers Love It */}
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
            Why Marketers Love Sneaky Redirects
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="flex items-center space-x-3 bg-slate-800/30 p-4 rounded-xl border border-slate-700/30"
              >
                <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-slate-200">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
            What Marketers Are Saying
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
              >
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-slate-300 text-lg mb-4 italic">"{testimonial.quote}"</p>
                <div className="flex items-center space-x-2">
                  <div>
                    <p className="text-white font-semibold">{testimonial.author}</p>
                    <p className="text-slate-400 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Pricing CTA */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 md:p-12 mb-8">
          <div className="text-center mb-8">
            <div className="inline-block bg-green-600/20 px-4 py-2 rounded-full mb-4 border border-green-500/30">
              <p className="text-green-300 text-sm font-semibold uppercase tracking-wide flex items-center justify-center space-x-2">
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                <span>Affordable Monthly Subscription</span>
              </p>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Just $9.97 Per Month
            </h2>
            
            <div className="inline-block relative mb-6">
              <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-8 border border-blue-500/30">
                <p className="text-6xl md:text-7xl font-bold text-white mb-2">
                  <span className="text-4xl align-top">$</span>9.97
                  <span className="text-3xl text-slate-400 font-normal">/mo</span>
                </p>
                <p className="text-slate-300 text-xl">Unlimited Access – Cancel Anytime</p>
              </div>
            </div>

            <p className="text-slate-400 text-lg mb-8">
              <strong className="text-white">No long-term commitment.</strong> Pay only while you use it.
            </p>
          </div>

          <div className="space-y-4 max-w-2xl mx-auto">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onGetStarted}
              className="w-full flex items-center justify-center space-x-2 px-8 py-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-2xl font-bold rounded-xl transition-all duration-200 shadow-lg shadow-blue-600/30"
            >
              <span>Start Your Subscription Today</span>
              <SafeIcon icon={FiTrendingUp} className="w-7 h-7" />
            </motion.button>

            <p className="text-center text-slate-400 text-sm">
              Secure Checkout – Instant Access – Cancel Anytime
            </p>

            <button
              onClick={onLogin}
              className="w-full flex items-center justify-center space-x-2 px-8 py-4 bg-slate-700/50 hover:bg-slate-700/70 text-slate-200 font-medium rounded-xl transition-all duration-200 border border-slate-600/50"
            >
              <SafeIcon icon={FiLogIn} className="w-5 h-5" />
              <span>Already have an account? Sign In</span>
            </button>
          </div>
        </div>

        {/* Perfect For Section */}
        <div className="bg-slate-800/30 border border-slate-700/30 rounded-2xl p-6 mb-8">
          <p className="text-slate-300 text-center">
            <strong className="text-white">Perfect for:</strong> Aged domains, SEO traffic, affiliate plays, or funnel optimization.
          </p>
        </div>

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm">
          <p className="mb-2">Sneaky Redirects – The Pro's Secret Weapon for Traffic Monetization</p>
          <p>© 2025 Sneaky Redirects. All rights reserved.</p>
        </div>
      </motion.div>
    </div>
  );
};

export default LandingPage;